import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Tag, Button, Space, Modal, Form, Input, InputNumber, Switch,
  Popconfirm, Tabs, Typography, message
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { adminAPI } from '../api/admin';

const { Text } = Typography;

function MissionsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getGameMissions();
      setItems(data.items || []);
    } catch (e) {
      message.error('خطا در دریافت ماموریت‌ها');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        await adminAPI.updateGameMission(editing._id, values);
        message.success('ماموریت بروزرسانی شد');
      } else {
        await adminAPI.createGameMission(values);
        message.success('ماموریت ایجاد شد');
      }
      setModalOpen(false);
      fetchList();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'عملیات ناموفق بود');
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminAPI.deleteGameMission(id);
      message.success('حذف شد');
      fetchList();
    } catch (e) {
      message.error('حذف ناموفق بود');
    }
  };

  const columns = [
    { title: 'کلید', dataIndex: 'key', width: 140 },
    { title: 'عنوان فارسی', dataIndex: 'farsiTitle' },
    { title: 'سال', dataIndex: 'year', width: 70 },
    { title: 'رویداد', dataIndex: 'eventName', width: 140 },
    { title: 'پاداش (ناتِ)', dataIndex: 'rewardKnuts', width: 110 },
    { title: 'وضعیت', dataIndex: 'isActive', width: 100, render: (v) => v ? <Tag color="success">فعال</Tag> : <Tag>غیرفعال</Tag> },
    {
      title: 'عملیات',
      width: 140,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm title="حذف این ماموریت؟" onConfirm={() => handleDelete(record._id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchList}>بروزرسانی</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>ماموریت جدید</Button>
          </Space>
        }
      >
        <Table rowKey="_id" columns={columns} dataSource={items} loading={loading} pagination={false} />
      </Card>

      <Modal
        title={editing ? 'ویرایش ماموریت' : 'ماموریت جدید'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="key" label="کلید یکتا" rules={[{ required: true }]}>
            <Input disabled={!!editing} />
          </Form.Item>
          <Form.Item name="year" label="سال (۱ تا ۷)" rules={[{ required: true }]}>
            <InputNumber min={1} max={7} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="title" label="عنوان (انگلیسی)" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="farsiTitle" label="عنوان فارسی" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="eventName" label="نام رویداد" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="farsiDescription" label="توضیحات فارسی">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="rewardKnuts" label="پاداش (نات)">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="isActive" label="فعال" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

function UserMissionsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchList = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getGameUserMissions({ page: p, limit: 20 });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      message.error('خطا در دریافت پیشرفت کاربران');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => { fetchList(page); }, [page, fetchList]);

  const columns = [
    { title: 'زمان تکمیل', dataIndex: 'completedAt', width: 160, render: (v) => moment(v).format('YYYY-MM-DD HH:mm') },
    {
      title: 'کاربر',
      dataIndex: 'user',
      render: (u) => u ? <Text>{u.farsiFirstName || u.firstName} {u.farsiLastName || u.lastName}</Text> : '—'
    },
    {
      title: 'ماموریت',
      dataIndex: 'mission',
      render: (m) => m ? <Text>{m.farsiTitle || m.title}</Text> : '—'
    },
    { title: 'انتخاب', dataIndex: 'choiceKey', width: 100 },
    { title: 'پاداش (نات)', dataIndex: 'rewardKnuts', width: 110 }
  ];

  return (
    <Card size="small" extra={<Button icon={<ReloadOutlined />} onClick={() => fetchList(page)}>بروزرسانی</Button>}>
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={items}
        loading={loading}
        pagination={{ current: page, pageSize: 20, total, showSizeChanger: false, onChange: setPage }}
      />
    </Card>
  );
}

export default function GameMissionManager() {
  return (
    <Tabs
      items={[
        { key: 'missions', label: 'ماموریت‌ها', children: <MissionsTab /> },
        { key: 'progress', label: 'پیشرفت کاربران', children: <UserMissionsTab /> }
      ]}
    />
  );
}
